import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import SEOHead from '../components/SEOHead';
import useAuthStore from '../store/authStore';
import { apiPost, apiGet } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';

// Input sanitization utility
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
};

// Validate email format
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate 2FA code format
const validate2FACode = (code) => {
  return /^\d{6}$/.test(code);
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function AdminLogin() {
  const { isDarkMode } = useDarkMode();
  const [step, setStep] = useState(1); // 1: login, 2: 2FA, 3: setup 2FA
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [qr, setQr] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const { user, isAdmin, loading: authLoading } = useAuthStore();

  // Check if user is already logged in and redirect appropriately
  useEffect(() => {
    if (!authLoading && user) {
      if (isAdmin) {
        // User is admin, redirect to admin dashboard
        navigate('/dashboard-a7f9b2e4-7c9e667d-6b93-11e7-9696-600308a46260');
      } else {
        // User is logged in but not admin, redirect to unauthorized
        navigate('/unauthorized');
      }
    }
  }, [user, isAdmin, authLoading, navigate]);

  // Step 1: Login with email/password
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Input validation and sanitization
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = sanitizeInput(password);
    
    if (!validateEmail(sanitizedEmail)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    
    if (!sanitizedPassword || sanitizedPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }
    
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      setLoading(false);
      return;
    }
    
    try {
      const res = await apiPost('/api/auth/login', { 
        email: sanitizedEmail, 
        password: sanitizedPassword, 
        recaptchaToken 
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed');
      // Check if admin and 2FA enabled
      if (data.user && data.user.isAdmin) {
        // Check 2FA status
        const meRes = await apiGet('/api/auth/me');
        const me = await meRes.json();
        if (me.twoFAEnabled) {
          setStep(2); // Show 2FA code input
        } else {
          // Start 2FA setup
          const setupRes = await apiPost('/api/admin/2fa/setup', { password: sanitizedPassword });
          const setup = await setupRes.json();
          if (!setupRes.ok) throw new Error(setup.message || '2FA setup failed');
          setQr(setup.qr);
          setBackupCodes(setup.backupCodes);
          setStep(3); // Show QR code and backup codes
        }
      } else {
        setError('Not an admin account');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify 2FA code
  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Input validation
    const sanitizedCode = sanitizeInput(code);
    if (!validate2FACode(sanitizedCode)) {
      setError('Please enter a valid 6-digit code');
      setLoading(false);
      return;
    }
    
    try {
      const res = await apiPost('/api/admin/2fa/login', { code: sanitizedCode });
      
      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Handle non-JSON response (likely server error)
        const textResponse = await res.text();
        // Non-JSON response
        throw new Error('Server error. Please try again or contact support.');
      }
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.message || '2FA verification failed');
      }
      
      // Update auth store with user data
      if (data.user) {
        const { setUser, setIsAdmin } = useAuthStore.getState();
        setUser(data.user);
        setIsAdmin(true);
      }
      // Force refresh auth state before redirecting
      window.location.href = '/dashboard-a7f9b2e4-7c9e667d-6b93-11e7-9696-600308a46260';
    } catch (err) {
      if (err.message.includes('JSON')) {
        setError('Server error. Please try again or contact support.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Setup 2FA (verify code)
  const handle2FASetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Input validation
    const sanitizedCode = sanitizeInput(code);
    if (!validate2FACode(sanitizedCode)) {
      setError('Please enter a valid 6-digit code');
      setLoading(false);
      return;
    }
    
    try {
      const res = await apiPost('/api/admin/2fa/verify', { code: sanitizedCode });
      
      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Handle non-JSON response (likely server error)
        const textResponse = await res.text();
        // Non-JSON response
        throw new Error('Server error. Please try again or contact support.');
      }
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '2FA setup failed');
      
      // Force refresh auth state before redirecting
      window.location.href = '/dashboard-a7f9b2e4-7c9e667d-6b93-11e7-9696-600308a46260';
    } catch (err) {
      if (err.message.includes('JSON')) {
        setError('Server error. Please try again or contact support.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-gray-200 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-500 dark:text-gray-400 text-base">Checking access...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] flex items-center justify-center">
      <SEOHead
        title="Admin Login | YTProud"
        description="Secure admin login portal for YTProud administrators. Two-factor authentication enabled."
        noindex={true}
        nofollow={true}
        canonical="https://ytproud.com/admin-x8k3m5n7-9f1d9504-6b93-11e7-9696-600308a46260"
      />
      <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl shadow-2xl p-9 max-w-sm w-full mx-4 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col items-center mb-6">
          <img src="/images/logo.png" alt="Admin" className="w-16 h-16 mb-2" />
          <h2 className="font-bold text-2xl m-0 text-gray-900 dark:text-gray-100">Admin Login</h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center">
            Admin access only. Regular users will be redirected.
          </p>
        </div>
        {step === 1 && (
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <input type="email" placeholder="Admin Email" value={email} onChange={e => setEmail(sanitizeInput(e.target.value))} required maxLength={100} className="p-3.5 rounded-lg border border-gray-200 dark:border-gray-600 text-base bg-gray-50 dark:bg-[#3a3a3a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <input type="password" placeholder="Password" value={password} onChange={e => setPassword(sanitizeInput(e.target.value))} required maxLength={128} minLength={6} className="p-3.5 rounded-lg border border-gray-200 dark:border-gray-600 text-base bg-gray-50 dark:bg-[#3a3a3a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <div className="self-center my-2">
              {RECAPTCHA_SITE_KEY ? (
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={token => setRecaptchaToken(token)}
                />
              ) : (
                <div className="text-red-600 dark:text-red-400 text-sm text-center">
                  reCAPTCHA not configured
                </div>
              )}
            </div>
            <button type="submit" disabled={loading} className="p-3.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-base border-none shadow-lg cursor-pointer mt-2 hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed">{loading ? 'Logging in...' : 'Login'}</button>
          </form>
        )}
        {step === 2 && (
          <form onSubmit={handle2FAVerify} className="flex flex-col gap-4">
            <input type="text" placeholder="Enter 2FA code" value={code} onChange={e => setCode(sanitizeInput(e.target.value))} required maxLength={6} pattern="[0-9]{6}" className="p-3.5 rounded-lg border border-gray-200 dark:border-gray-600 text-base bg-gray-50 dark:bg-[#3a3a3a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <button type="submit" disabled={loading} className="p-3.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-base border-none shadow-lg cursor-pointer mt-2 hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed">{loading ? 'Verifying...' : 'Verify 2FA'}</button>
          </form>
        )}
        {step === 3 && (
          <form onSubmit={handle2FASetup} className="flex flex-col gap-4">
            <div className="mb-2 text-center">
              <div className="font-medium mb-2 text-gray-900 dark:text-gray-100">Scan this QR code with Google Authenticator:</div>
              <img src={qr} alt="QR Code" className="w-45 h-45 mx-auto my-4 rounded-lg shadow-lg" />
              <div className="font-medium mt-3 mb-1 text-gray-900 dark:text-gray-100">Backup codes (save these!):</div>
              <ul className="font-mono text-sm bg-gray-50 dark:bg-[#3a3a3a] rounded-lg p-3 m-0 list-none grid grid-cols-2 gap-1 text-gray-900 dark:text-gray-100">
                {backupCodes.map(code => <li key={code}>{code}</li>)}
              </ul>
            </div>
            <input type="text" placeholder="Enter 2FA code from app" value={code} onChange={e => setCode(sanitizeInput(e.target.value))} required maxLength={6} pattern="[0-9]{6}" className="p-3.5 rounded-lg border border-gray-200 dark:border-gray-600 text-base bg-gray-50 dark:bg-[#3a3a3a] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <button type="submit" disabled={loading} className="p-3.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-base border-none shadow-lg cursor-pointer mt-2 hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed">{loading ? 'Enabling...' : 'Enable 2FA'}</button>
          </form>
        )}
        {error && <div className="text-red-600 dark:text-red-400 mt-4.5 text-center font-medium">{error}</div>}
      </div>
    </div>
  );
} 